import { useRef, useState } from 'react'
import { useIntl } from 'react-intl'

import { fetchSettingsFlow, submitRecoveryCode, submitSettingsPassword } from '@utils/kratos.utils'
import { logClientError, translateClientError } from '@i18n/kratos/kratos-client-error'
import { findFieldError, findInput } from '@utils/kratos-nodes.utils'

// Kratos enforces the real policy server-side; this only keeps a half-filled
// form from spending the one-time code on a request that cannot succeed.
const MIN_PASSWORD_LENGTH = 8

interface Params {
  flowId: string | null
  code: string | null
  returnTo: string
  // Code never left our hands — a fresh one is what the invitee needs.
  onLinkDead: () => void
  // Code is gone and a session exists. A new code would be useless here; the
  // invitee is one password away from done, which /settings already handles.
  onCodeSpent: () => void
}

interface State {
  submitting: boolean
  passwordError: string | null
  formError: string | null
}

const IDLE: State = {
  submitting: false,
  passwordError: null,
  formError: null,
}

export const useSetInitialPassword = ({
  flowId,
  code,
  returnTo,
  onLinkDead,
  onCodeSpent,
}: Params) => {
  const intl = useIntl()
  const [state, setState] = useState<State>(IDLE)
  // Survives a rejected password: the code is already spent by then, so a
  // second attempt must reuse the settings flow instead of redeeming again.
  const settingsFlowRef = useRef<string | null>(null)
  // A ref, not `state.submitting` — the state read would come from the render
  // that produced the handler, and a one-time credential is not something to
  // protect with a stale closure.
  const inFlightRef = useRef(false)

  // The error judges a password the invitee has now changed, so it stops being
  // true the moment they edit the field. Returning `prev` unchanged keeps the
  // common keystroke (no error showing) from re-rendering.
  const clearPasswordError = (): void => {
    setState((prev) => (prev.passwordError === null ? prev : { ...prev, passwordError: null }))
  }

  const submit = async (password: string, confirmation: string): Promise<void> => {
    if (inFlightRef.current) {
      return
    }
    if (password.length < MIN_PASSWORD_LENGTH) {
      setState({
        ...IDLE,
        passwordError: intl.formatMessage(
          { id: 'invite.error.tooShort' },
          { count: MIN_PASSWORD_LENGTH },
        ),
      })
      return
    }
    if (password !== confirmation) {
      setState({ ...IDLE, passwordError: intl.formatMessage({ id: 'invite.error.mismatch' }) })
      return
    }

    inFlightRef.current = true
    setState({ ...IDLE, submitting: true })

    const fail = (message: string): void => {
      setState({ ...IDLE, formError: message })
    }

    try {
      if (settingsFlowRef.current === null) {
        // Truthiness, not a null check: a mangled `?code=` arrives as an empty
        // string. Nothing to redeem means a fresh link is the only way on.
        if (!flowId || !code) {
          onLinkDead()
          return
        }
        const redeemed = await submitRecoveryCode(flowId, code)
        switch (redeemed.kind) {
          case 'ok':
            settingsFlowRef.current = redeemed.settingsFlowId
            break
          // A code that rode in on the URL cannot be corrected here — the
          // invitee needs a fresh link either way.
          case 'dead':
          case 'rejected':
            onLinkDead()
            return
          case 'error':
            logClientError(redeemed.code, redeemed.message)
            fail(translateClientError(intl, redeemed.code))
            return
        }
      }

      const settingsFlow = await fetchSettingsFlow(settingsFlowRef.current)
      if (settingsFlow.kind !== 'ok') {
        if (settingsFlow.kind === 'error') {
          logClientError(settingsFlow.code, settingsFlow.message)
          fail(translateClientError(intl, settingsFlow.code))
          return
        }
        // Expired flow or an identity whose TOTP forces AAL2 — either way the
        // code is spent and only /settings can finish the job.
        onCodeSpent()
        return
      }
      const csrfAttrs = findInput(settingsFlow.flow.ui.nodes, 'csrf_token')

      const updated = await submitSettingsPassword(
        settingsFlowRef.current,
        password,
        csrfAttrs ? String(csrfAttrs.value ?? '') : null,
      )
      switch (updated.kind) {
        case 'ok':
          window.location.assign(returnTo)
          return
        case 'rejected':
          setState({
            ...IDLE,
            passwordError:
              findFieldError(updated.flow.ui.nodes, 'password', intl) ??
              intl.formatMessage({ id: 'invite.error.rejected' }),
          })
          return
        case 'reauth_required':
          onCodeSpent()
          return
        case 'error':
          logClientError(updated.code, updated.message)
          fail(translateClientError(intl, updated.code))
      }
    } catch (err) {
      // Anything unforeseen must still clear `submitting`, or the page sits on
      // "Setting password…" for good with the code already spent.
      logClientError('upstream', err instanceof Error ? err.message : 'invite submit failed')
      fail(translateClientError(intl, 'upstream'))
    } finally {
      inFlightRef.current = false
    }
  }

  return { ...state, submit, clearPasswordError }
}
